import { Step, TypedStep } from "./step";

/**
 * Editor state renames model properties (options -> choices), so this matches
 * the `*UUID` naming convention rather than the element-id registry.
 */
const ID_KEY = /UUID$/;

function isMintable(key: string, value: unknown): boolean {
  return ID_KEY.test(key) && value !== undefined;
}

/** Rebuilding a class instance (InputState.exampleImage is a URL) empties it. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const prototype: unknown = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasId(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some(hasId);
  }
  if (!isPlainObject(value)) {
    return false;
  }
  return Object.entries(value).some(
    ([key, held]) => isMintable(key, held) || hasId(held),
  );
}

function reminted(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(reminted);
  }
  if (!isPlainObject(value)) {
    return value;
  }
  return Object.fromEntries(
    Object.entries(value).map(([key, held]) =>
      isMintable(key, held)
        ? [key, crypto.randomUUID()]
        : [key, reminted(held)],
    ),
  );
}

/** Never mutates: paste hands the same state object to copy and original. */
export function remintAllStateUuids(step: Step): Step {
  const state = step.state;
  if (!hasId(state)) {
    return step;
  }
  return new TypedStep(step.type, reminted(state));
}
